import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Package,
  Truck,
  Users,
  IndianRupee,
  BadgeIndianRupee,
  Tags,
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ArrowRight,
  RefreshCcw,
  UserCheck,
  Boxes,
  Megaphone,
  Image as ImageIcon,
  Building2,
  BarChart3,
  ChevronDown,
} from "lucide-react";

import { orderAnalyticsService } from "../../../services/orderAnalytics.service";
import { NotificationsService } from "../../../services/notifications.service";
import { dashboardSummaryService } from "../../../services/dashboardSummary.service";
import { ReportsService } from "../../../services/reports.service";
import { settingsService } from "../../../services/settings.service";
import dashboardGroceryBag from "../../../assets/dashboard-grocery-bag.png";

const THEME = "#d86a78";
const ORANGE = "#ef9440";
const GREEN = "#6bbf59";

const DASHBOARD_PERIOD_OPTIONS = [
  { value: "month", label: "Current Month" },
  { value: "all", label: "All Time" },
];

function getDashboardAnalyticsRange(period) {
  if (period !== "month") return {};

  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());

  return {
    fromDate: `${year}-${month}-01`,
    toDate: `${year}-${month}-${day}`,
  };
}

function warehouseRowsOf(res) {
  return res?.warehouses || res?.data?.warehouses || res?.items || res?.data?.items || [];
}

function scopedUsersSummary(kpis = {}) {
  return {
    totalUsers: toNumber(kpis.total_users),
    activeUsers: toNumber(kpis.active_users ?? kpis.ordering_users),
    inactiveUsers: toNumber(kpis.inactive_users),
    raw: kpis,
  };
}

function scopedDriversSummary(kpis = {}) {
  const active = toNumber(kpis.active_drivers);
  const total = toNumber(kpis.total_drivers);
  const inactive = toNumber(kpis.inactive_drivers ?? Math.max(total - active, 0));
  return {
    totalDrivers: total,
    activeDrivers: active,
    inactiveDrivers: inactive,
    busyDrivers: toNumber(kpis.busy_drivers),
    offlineDrivers: Math.max(inactive, toNumber(kpis.offline_drivers)),
    raw: kpis,
  };
}

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function DashboardFilterSelect({ icon: Icon, label, value, onChange, options, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedOption = options.find((option) => String(option.value) === String(value)) || options[0];

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "group/control flex h-[62px] w-full items-center gap-2.5 rounded-[15px] border bg-white/95 px-3.5 text-left shadow-[0_8px_20px_rgba(34,45,82,0.075)] backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-[#efc3ca] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d86a78]/25",
          open ? "border-[#efb7c0] ring-2 ring-[#d86a78]/15" : "border-white ring-1 ring-slate-200/80"
        )}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-[#fff0f2] text-[#e65f73]">
          <Icon size={15} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="mb-0.5 block text-[9px] font-black uppercase tracking-[0.09em] text-slate-400">{label}</span>
          <span className="block truncate text-[12px] font-black text-slate-800">{selectedOption?.label}</span>
        </span>
        <ChevronDown size={15} aria-hidden="true" className={cn("shrink-0 text-slate-700 transition-transform duration-300", open && "rotate-180 text-[#d86a78]")} />
      </button>

      {open ? (
        <div className="dashboard-filter-menu absolute left-0 right-0 top-[calc(100%+8px)] z-[80] overflow-hidden rounded-[16px] border border-[#efd8dc] bg-white/95 p-1.5 shadow-[0_18px_45px_rgba(39,48,78,0.18)] backdrop-blur-xl" role="listbox" aria-label={ariaLabel}>
          <div className="dashboard-filter-options max-h-56 space-y-1 overflow-y-auto p-0.5">
            {options.map((option) => {
              const isSelected = String(option.value) === String(value);
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-[12px] font-bold transition-colors",
                    isSelected ? "bg-[#fff0f2] text-[#d65568]" : "text-slate-700 hover:bg-slate-50 hover:text-slate-950"
                  )}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected ? <CheckCircle2 size={15} className="shrink-0 text-[#d86a78]" /> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatCurrency(value) {
  const num = Number(value || 0);

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(num) ? num : 0);
}

function shortDashboardMoney(value) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "₹0";
  if (Math.abs(amount) >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (Math.abs(amount) >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (Math.abs(amount) >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${Math.round(amount)}`;
}

function pickArray(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data?.rows)) return res.data.rows;
  if (Array.isArray(res?.data?.orders)) return res.data.orders;
  if (Array.isArray(res?.data?.products)) return res.data.products;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.orders)) return res.orders;
  if (Array.isArray(res?.users)) return res.users;
  if (Array.isArray(res?.partners)) return res.partners;
  if (Array.isArray(res?.products)) return res.products;
  if (Array.isArray(res?.notifications)) return res.notifications;
  if (Array.isArray(res?.broadcasts)) return res.broadcasts;
  if (Array.isArray(res?.posters)) return res.posters;
  return [];
}

function pickTotal(res, fallbackItems = []) {
  return Number(
    res?.total ||
    res?.count ||
    res?.meta?.total ||
    res?.pagination?.total ||
    res?.data?.total ||
    res?.data?.count ||
    fallbackItems.length ||
    0
  );
}

function getSettledValue(result, fallback = null) {
  return result?.status === "fulfilled" ? result.value : fallback;
}

function toNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function hasSummaryValue(obj, key) {
  return obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== "";
}

function getOrderAmount(order = {}) {
  return toNumber(
    order.grandTotal ??
    order.grand_total ??
    order.totalAmount ??
    order.total_amount ??
    order.amount ??
    order.netAmount ??
    order.net_amount ??
    0
  );
}

function getOrderStatus(order = {}) {
  return String(
    order.status || order.orderStatus || order.order_status || ""
  ).toUpperCase();
}

function getDriverStatus(driver = {}) {
  return String(
    driver.status || driver.partnerStatus || driver.partner_status || ""
  ).toUpperCase();
}

function getStockQty(item = {}) {
  return toNumber(
    item.qtyAvailable ??
    item.qty_available ??
    item.availableQty ??
    item.available_qty ??
    item.quantity ??
    item.qty ??
    item.stock ??
    0
  );
}

function getStockThreshold(item = {}) {
  return toNumber(
    item.lowStockThreshold ??
    item.low_stock_threshold ??
    item.threshold ??
    item.minStock ??
    item.min_stock ??
    0
  );
}

function isDateWithinDays(value, days = 7) {
  if (!value) return false;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;

  const now = new Date();
  const diff = d.getTime() - now.getTime();

  return diff >= 0 && diff <= days * 24 * 60 * 60 * 1000;
}

function formatDate(value) {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getTrendPoint(item = {}) {
  return toNumber(
    item.totalSales ??
    item.total_sales ??
    item.totalRevenue ??
    item.total_revenue ??
    item.revenue ??
    item.amount ??
    item.total ??
    0
  );
}

function getTrendOrderCount(item = {}) {
  return toNumber(
    item.totalOrders ??
    item.total_orders ??
    item.orderCount ??
    item.order_count ??
    item.orders ??
    item.count ??
    0
  );
}

function getTrendLabel(item = {}) {
  const raw = item.bucket || item.date || item.day || item.month || item.label || item.period;

  if (!raw) return "-";

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);

  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
}

function getEmployeeApprovalStatus(item = {}) {
  return String(
    item.approvalStatus ||
    item.approval_status ||
    item.raw?.approval_status ||
    item.raw?.approvalStatus ||
    ""
  ).toUpperCase();
}

function isActiveRow(item = {}) {
  const status = String(item.status || item.rawStatus || "").toUpperCase();
  return item.isActive !== false && !["BLOCKED", "INACTIVE", "DEACTIVATED", "DELETED"].includes(status);
}

function StatCard({ icon: Icon, value, label, change, tone = "pink", compact = false }) {
  const tones = {
    pink: {
      icon: "bg-[#fff0f3] text-[#ef4767] shadow-[0_8px_20px_rgba(239,71,103,0.14)]",
      accent: "from-[#ff536f] to-[#e65476]",
      surface: "from-[#fff9fa] via-white to-[#fff1f4]",
      glow: "bg-[#ff6b86]/10",
      color: "#ff5b78",
    },
    green: {
      icon: "bg-emerald-50 text-emerald-600 shadow-[0_8px_20px_rgba(16,185,129,0.12)]",
      accent: "from-emerald-400 to-emerald-600",
      surface: "from-[#f9fffc] via-white to-[#effcf6]",
      glow: "bg-emerald-400/10",
      color: "#18b981",
    },
    amber: {
      icon: "bg-amber-50 text-amber-600 shadow-[0_8px_20px_rgba(245,158,11,0.13)]",
      accent: "from-amber-400 to-orange-500",
      surface: "from-[#fffdf8] via-white to-[#fff7ea]",
      glow: "bg-orange-400/10",
      color: "#f59e0b",
    },
    blue: {
      icon: "bg-sky-50 text-sky-600 shadow-[0_8px_20px_rgba(14,165,233,0.12)]",
      accent: "from-sky-400 to-blue-600",
      surface: "from-[#f8fcff] via-white to-[#eef7ff]",
      glow: "bg-sky-400/10",
      color: "#1da1f2",
    },
    violet: {
      icon: "bg-violet-50 text-violet-600 shadow-[0_8px_20px_rgba(124,58,237,0.12)]",
      accent: "from-violet-400 to-purple-600",
      surface: "from-[#fbfaff] via-white to-[#f4f0ff]",
      glow: "bg-violet-400/10",
      color: "#8b5cf6",
    },
    slate: {
      icon: "bg-slate-100 text-slate-600",
      accent: "from-slate-400 to-slate-600",
      surface: "from-slate-50 via-white to-slate-100/80",
      glow: "bg-slate-400/10",
      color: "#64748b",
    },
  };
  const style = tones[tone] || tones.pink;

  return (
    <div className={`group relative min-w-0 overflow-hidden rounded-[18px] border border-white/90 bg-gradient-to-br ${style.surface} shadow-[0_12px_30px_rgba(37,48,78,0.07)] ring-1 ring-slate-200/65 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_42px_rgba(37,48,78,0.12)] ${compact ? "min-h-[138px] p-4" : "min-h-[152px] p-5"}`}>
      <span className="absolute inset-y-0 left-0 w-[2px]" style={{ backgroundColor: style.color }} />
      <span className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl ${style.glow}`} />
      <svg aria-hidden="true" viewBox="0 0 280 72" preserveAspectRatio="none" className="dashboard-stat-wave pointer-events-none absolute bottom-0 right-0 h-[64px] w-[58%] opacity-70">
        <path d="M0 65 C20 63 25 45 43 45 C62 45 64 58 82 56 C103 54 105 27 128 27 C149 27 151 47 171 43 C194 39 196 10 219 10 C241 10 245 39 260 33 C269 30 274 24 280 19 L280 72 L0 72 Z" fill={style.color} fillOpacity="0.08" />
        <path d="M0 65 C20 63 25 45 43 45 C62 45 64 58 82 56 C103 54 105 27 128 27 C149 27 151 47 171 43 C194 39 196 10 219 10 C241 10 245 39 260 33 C269 30 274 24 280 19" fill="none" stroke={style.color} strokeOpacity="0.23" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      <div className="relative z-10 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.13em] text-slate-400">{label}</p>
          <div
            title={String(value ?? "")}
            className={`${compact ? "mt-2 text-[22px]" : "mt-3 text-[clamp(22px,1.7vw,29px)]"} truncate font-black leading-none tracking-[-0.045em] text-[#101828] tabular-nums`}
          >
            {value}
          </div>
          <p className="mt-2 truncate text-[11px] font-semibold text-slate-500">{change}</p>
        </div>
        <div className={`grid ${compact ? "h-11 w-11 rounded-[14px]" : "h-12 w-12 rounded-[15px]"} shrink-0 place-items-center border border-white/90 ${style.icon} ring-1 ring-white/70 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:scale-105`}>
          <Icon size={compact ? 17 : 20} />
        </div>
      </div>
      <div className={`absolute ${compact ? "bottom-4 left-4 w-[116px]" : "bottom-4 left-5 w-[154px]"} z-10 h-1.5 overflow-hidden rounded-full bg-white/70 shadow-inner`}>
        <span className="block h-full w-[48%] rounded-full transition-all duration-500 group-hover:w-[62%]" style={{ backgroundColor: style.color }} />
      </div>
    </div>
  );
}

function DonutChart({ title, centerValue, centerLabel, rows, variant = "orders" }) {
  const colors =
    variant === "orders"
      ? [ORANGE, GREEN, "#a7a7a7", THEME]
      : [THEME, ORANGE, "#a7a7a7", GREEN];

  const safeRows = Array.isArray(rows) ? rows : [];

 const values = safeRows.map((row) =>
  Math.max(0, Number(row?.value || 0))
);

const rowsTotal = values.reduce((sum, value) => sum + value, 0);

// Center value is the real total order/driver count.
const chartTotal = Math.max(
  Number(centerValue || 0),
  rowsTotal,
  1
);

const radius = 44;
const circumference = 2 * Math.PI * radius;

const visibleSegments = values.filter((value) => value > 0).length;
const gap = visibleSegments > 1 ? 4 : 0;

  let runningOffset = 0;

  const segments = values.map((value, index) => {
    const rawLength =
  chartTotal > 0 ? (value / chartTotal) * circumference : 0;
    const length = value > 0 ? Math.max(rawLength - gap, 1) : 0;

    const segment = {
      value,
      color: colors[index] || THEME,
      dasharray: `${length} ${circumference - length}`,
      dashoffset: -runningOffset,
    };

    runningOffset += rawLength;

    return segment;
  });

  return (
    <div className="group rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70 transition-shadow hover:shadow-[0_18px_42px_rgba(15,23,42,0.09)]">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[16px] font-black tracking-[-0.02em] text-[#101828]">{title}</h3>
        <span className="rounded-full bg-slate-50 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">Live data</span>
      </div>

      <div className="mt-5 grid grid-cols-1 items-center gap-5 md:grid-cols-[145px_1fr]">
        <div className="relative mx-auto h-[135px] w-[135px]">
          <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
            <circle
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke="#eef2f7"
              strokeWidth="14"
            />

           {rowsTotal > 0
              ? segments.map((segment, index) => {
                if (!segment.value) return null;

                return (
                  <circle
                    key={`${safeRows[index]?.label || "segment"}-${index}`}
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth="14"
                    strokeLinecap="round"
                    className="drop-shadow-[0_3px_5px_rgba(15,23,42,0.12)]"
                    strokeDasharray={segment.dasharray}
                    strokeDashoffset={segment.dashoffset}
                  />
                );
              })
              : null}
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <div className="text-[22px] font-black tracking-[-0.04em] text-[#101828]">
              {centerValue}
            </div>
            <div className="mt-0.5 text-[10px] font-semibold text-slate-500">{centerLabel}</div>
          </div>
        </div>

        <div className="space-y-3">
          {safeRows.map((row, index) => (
            <div
              key={`${row.label}-${index}`}
              className="grid grid-cols-[1fr_auto_auto] gap-3 text-[12px]"
            >
              <div className="flex items-center gap-2 text-slate-600">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: colors[index] || THEME }}
                />
                {row.label}
              </div>

              <div className="font-semibold text-slate-700">{row.value}</div>
              <div className="text-slate-500">{row.percent}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



function AdminQuickSummary({
  productCount = 0,
  pendingEmployees = 0,
  activePosters = 0,
  activeBroadcasts = 0,
  onProducts,
  onEmployees,
  onPosters,
  onBroadcasts,
}) {
  const items = [
    {
      icon: Boxes,
      label: "Catalog products",
      count: productCount,
      onClick: onProducts,
      tone: "border-rose-100 bg-gradient-to-br from-rose-50/80 to-white text-rose-500",
    },
    {
      icon: UserCheck,
      label: "Pending approvals",
      count: pendingEmployees,
      onClick: onEmployees,
      tone: "border-amber-100 bg-gradient-to-br from-amber-50/80 to-white text-amber-500",
    },
    {
      icon: ImageIcon,
      label: "Active posters",
      count: activePosters,
      onClick: onPosters,
      tone: "border-violet-100 bg-gradient-to-br from-violet-50/80 to-white text-violet-500",
    },
    {
      icon: Megaphone,
      label: "Active broadcasts",
      count: activeBroadcasts,
      onClick: onBroadcasts,
      tone: "border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white text-emerald-500",
    },
  ];

  return (
    <div className="rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70">
      <div className="flex items-center justify-between">
        <h3 className="text-[16px] font-black tracking-[-0.02em] text-[#101828]">Admin quick summary</h3>
        <span className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.12)]" />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((item) => {
          const Icon = item.icon;

          return (
            <button
              key={item.label}
              type="button"
              onClick={item.onClick}
              className={`group/summary rounded-[16px] border p-4 text-left shadow-[0_8px_18px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(15,23,42,0.07)] ${item.tone}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-[11px] border border-white bg-white/90 shadow-sm">
                  <Icon size={17} />
                </div>

                <div className="text-[22px] font-black tracking-[-0.04em] text-[#101828]">
                  {item.count}
                </div>
              </div>

              <div className="mt-3 text-[12px] font-semibold text-slate-600">
                {item.label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StockHealth({
  lowStockCount = 0,
  expiringSoonCount = 0,
  outOfStockCount = 0,
  onLowStock,
  onExpiringSoon,
  onOutOfStock,
}) {
  const items = [
    {
      icon: AlertTriangle,
      label: "Low stock",
      count: lowStockCount,
      onClick: onLowStock,
    },
    {
      icon: CalendarClock,
      label: "Expiring soon",
      count: expiringSoonCount,
      onClick: onExpiringSoon,
    },
    {
      icon: Package,
      label: "Out of stock",
      count: outOfStockCount,
      onClick: onOutOfStock,
    },
  ];

  return (
    <div className="rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70">
      <div className="flex items-center justify-between">
        <h3 className="text-[16px] font-black tracking-[-0.02em] text-[#101828]">Stock health</h3>
        <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-rose-500">Attention</span>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {items.map((item) => {
          const Icon = item.icon;

          return (
            <div key={item.label} className="rounded-[16px] border border-slate-100 bg-gradient-to-b from-slate-50/80 to-white p-4 shadow-[0_8px_18px_rgba(15,23,42,0.035)]">
              <div className="flex h-9 w-9 items-center justify-center rounded-[11px] bg-[#fff1f3] text-[#e65a72] shadow-sm">
                <Icon size={15} />
              </div>

              <div className="mt-3 text-[12px] text-slate-600">{item.label}</div>

              <div className="mt-2 text-[22px] font-black tracking-[-0.04em] text-[#101828]">
                {item.count}
              </div>

              <button
                type="button"
                onClick={item.onClick}
                className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-[#d86a78]"
              >
                View stock <ArrowRight size={12} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrendRevenue({ items = [] }) {
  const points = items.slice(-7);
  const values = points.map(getTrendPoint);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = Math.max(max - min, 1);
  const total = values.reduce((sum, value) => sum + value, 0);

  const xFor = (index) => 58 + index * (330 / Math.max(points.length - 1, 1));
  const yFor = (value) => 150 - ((value - min) / range) * 112;
  const path = points
    .map((item, index) => `${index === 0 ? "M" : "L"}${xFor(index)} ${yFor(getTrendPoint(item))}`)
    .join(" ");
  const area = points.length ? `${path} L${xFor(points.length - 1)} 158 L58 158 Z` : "";

  return (
    <div className="rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">Revenue movement</p>
          <h3 className="mt-1 text-[17px] font-black text-slate-950">Collection trend</h3>
          <p className="mt-1 text-[11px] font-medium text-slate-500">Actual daily collection data · latest 7 points</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Displayed total</p>
          <p className="mt-1 text-lg font-black text-[#d86a78]">{formatCurrency(total)}</p>
        </div>
      </div>

      {!points.length ? (
        <div className="mt-5 flex h-[220px] items-center justify-center rounded-[16px] border border-dashed border-slate-200 text-[13px] font-semibold text-slate-400">
          No collection trend data for this scope
        </div>
      ) : (
        <svg viewBox="0 0 420 205" className="mt-4 h-[220px] w-full overflow-visible" role="img" aria-label="Daily collection trend">
          {[0, 0.5, 1].map((ratio) => {
            const value = min + (max - min) * (1 - ratio);
            const y = 38 + ratio * 112;
            return (
              <g key={ratio}>
                <line x1="58" x2="397" y1={y} y2={y} stroke="#e9edf2" strokeWidth="1" strokeDasharray="4 5" />
                <text x="4" y={y + 4} fontSize="9" fill="#94a3b8">{shortDashboardMoney(value)}</text>
              </g>
            );
          })}
          <path d={area} fill="#d86a78" opacity="0.08" />
          <path d={path} fill="none" stroke={THEME} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          {points.map((item, index) => {
            const value = getTrendPoint(item);
            const x = xFor(index);
            const y = yFor(value);
            return (
              <g key={`${getTrendLabel(item)}-${index}`}>
                <circle cx={x} cy={y} r="4" fill="white" stroke={THEME} strokeWidth="3" />
                <text x={x} y="184" textAnchor="middle" fontSize="9" fill="#64748b">{getTrendLabel(item)}</text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function OrderTrend({ items = [] }) {
  const points = items.slice(-7);
  const max = Math.max(...points.map(getTrendOrderCount), 1);
  const total = points.reduce((sum, item) => sum + getTrendOrderCount(item), 0);

  return (
    <div className="rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">Order velocity</p>
          <h3 className="mt-1 text-[17px] font-black text-slate-950">Order trend</h3>
          <p className="mt-1 text-[11px] font-medium text-slate-500">Orders created in each daily bucket</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Displayed orders</p>
          <p className="mt-1 text-lg font-black text-slate-950">{total}</p>
        </div>
      </div>

      {!points.length ? (
        <div className="mt-5 flex h-[220px] items-center justify-center rounded-[16px] border border-dashed border-slate-200 text-[13px] font-semibold text-slate-400">
          No order trend data for this scope
        </div>
      ) : (
        <svg viewBox="0 0 420 205" className="mt-4 h-[220px] w-full" role="img" aria-label="Daily order counts">
          {[0, 0.5, 1].map((ratio) => {
            const y = 38 + ratio * 112;
            const value = Math.round(max * (1 - ratio));
            return (
              <g key={ratio}>
                <line x1="46" x2="400" y1={y} y2={y} stroke="#e9edf2" strokeWidth="1" strokeDasharray="4 5" />
                <text x="10" y={y + 4} fontSize="9" fill="#94a3b8">{value}</text>
              </g>
            );
          })}
          {points.map((item, index) => {
            const orders = getTrendOrderCount(item);
            const h = Math.max((orders / max) * 112, orders > 0 ? 8 : 2);
            const slot = 330 / Math.max(points.length, 1);
            const width = Math.min(28, slot * 0.58);
            const x = 56 + index * slot + (slot - width) / 2;
            return (
              <g key={`${getTrendLabel(item)}-${index}`}>
                <rect x={x} y={150 - h} width={width} height={h} rx="7" fill="#d86a78" opacity={orders > 0 ? 1 : 0.2} />
                <text x={x + width / 2} y={Math.max(150 - h - 7, 20)} textAnchor="middle" fontSize="9" fontWeight="700" fill="#475569">{orders}</text>
                <text x={x + width / 2} y="184" textAnchor="middle" fontSize="9" fill="#64748b">{getTrendLabel(item)}</text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function RecentActivities({ items = [], onViewAll }) {
  const rows = items.slice(0, 6);

  return (
    <div className="rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70">
      <div className="flex items-center justify-between">
        <h3 className="text-[16px] font-black tracking-[-0.02em] text-[#101828]">Recent activities</h3>

        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex items-center gap-1 rounded-full border border-rose-100 bg-rose-50 px-3 py-1.5 text-[10px] font-black text-rose-500 transition hover:bg-rose-100"
        >
          View all <ArrowRight size={11} />
        </button>
      </div>

      {!rows.length ? (
        <div className="mt-4 flex h-[150px] items-center justify-center rounded-[14px] border border-dashed border-[#f2c6cc] text-[13px] text-slate-400">
          No recent activities
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {rows.map((item, index) => (
            <div
              key={item.id || index}
              className="grid grid-cols-[18px_1fr_auto] items-center gap-2 text-[12px]"
            >
              <CheckCircle2 size={15} className="text-green-500" />

              <div className="truncate text-slate-700">
                {item.title || item.body || item.message || item.type || "Activity"}
              </div>

              <div className="whitespace-nowrap text-slate-500">
                {formatDate(item.createdAt || item.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecentOrdersTable({ items = [], onViewAll }) {
  const rows = items.slice(0, 6);

  return (
    <div className="rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[16px] font-black tracking-[-0.02em] text-[#101828]">Recent orders</h3>
          <p className="mt-1 text-[10px] font-semibold text-slate-400">Latest transaction activity</p>
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1.5 text-[10px] font-black text-rose-500 transition hover:bg-rose-100"
        >
          View all <ArrowRight size={11} />
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-[12px]">
          <thead>
            <tr className="bg-gradient-to-r from-[#fff0f3] via-[#fff5f7] to-[#fff0f3] text-[#df5f76]">
              {["Order", "Customer", "Status", "Payment", "Items", "Total", "Created"].map(
                (head, index) => (
                  <th
                    key={head}
                    className={cn(
                      "px-4 py-3 font-extrabold",
                      index === 0 && "rounded-l-[10px]",
                      index === 6 && "rounded-r-[10px]"
                    )}
                  >
                    {head}
                  </th>
                )
              )}
            </tr>
          </thead>

          <tbody>
            {!rows.length ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  No recent orders
                </td>
              </tr>
            ) : (
              rows.map((item, index) => (
                <tr key={item.id || index} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-semibold text-slate-700">
                    {item.orderNumber || item.order_number || item.orderId || item.order_id || item.id || "-"}
                  </td>

                  <td className="px-4 py-3 text-slate-600">
                    {item.customerName ||
                      item.customer_name ||
                      item.userName ||
                      item.user_name ||
                      item.name ||
                      "-"}
                  </td>

                  <td className="px-4 py-3">
                    <span className="rounded-full border border-rose-100 bg-rose-50 px-3 py-1 text-[10px] font-black text-rose-500">
                      {getOrderStatus(item) || "-"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-[10px] font-black text-emerald-600">
                      {item.paymentStatus || item.payment_status || "-"}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-slate-600">
                    {item.itemCount || item.item_count || item.itemsCount || item.items_count || 0}
                  </td>

                  <td className="px-4 py-3 font-bold text-slate-700">
                    {formatCurrency(getOrderAmount(item))}
                  </td>

                  <td className="px-4 py-3 text-slate-600">
                    {formatDate(item.createdAt || item.created_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TopProductsTable({ items = [], onViewAll }) {
  const rows = items.slice(0, 5);

  return (
    <div className="rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[16px] font-black tracking-[-0.02em] text-[#101828]">Top selling products</h3>
          <p className="mt-1 text-[10px] font-semibold text-slate-400">Best performing catalog items</p>
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black text-emerald-600 transition hover:bg-emerald-100"
        >
          View all <ArrowRight size={11} />
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[310px] text-left text-[12px]">
          <thead>
            <tr className="bg-gradient-to-r from-[#fff0f3] via-[#fff5f7] to-[#fff0f3] text-[#df5f76]">
              <th className="rounded-l-[10px] px-4 py-3 font-extrabold">Product</th>
              <th className="px-4 py-3 font-extrabold">Order</th>
              <th className="px-4 py-3 font-extrabold">Revenue</th>
              <th className="rounded-r-[10px] px-4 py-3 font-extrabold">Profit</th>
            </tr>
          </thead>

          <tbody>
            {!rows.length ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                  No product data
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={row.productId || row.product_id || row.sku || index}
                  className="border-b border-slate-100 last:border-0"
                >
                  <td className="px-4 py-3 text-slate-600">
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-gradient-to-br from-amber-50 to-orange-50 text-orange-500 shadow-sm ring-1 ring-orange-100">
                        <Package size={15} />
                      </span>
                      <span className="font-semibold text-slate-700">
                        {row.productName || row.product_name || row.name || row.title || "-"}
                      </span>
                    </div>
                  </td>

                  <td className="px-4 py-3 text-slate-600">
                    {row.orderCount || row.order_count || row.orders || row.count || 0}
                  </td>

                  <td className="px-4 py-3 font-semibold text-green-600">
                    {formatCurrency(
                      row.salesAmount ||
                      row.sales_amount ||
                      row.productSales ||
                      row.product_sales ||
                      row.itemSales ||
                      row.item_sales ||
                      row.totalSales ||
                      row.total_sales ||
                      row.totalRevenue ||
                      row.total_revenue ||
                      row.revenue ||
                      0
                    )}
                  </td>

                  <td className="px-4 py-3 font-semibold text-slate-700">
                    {formatCurrency(row.actualProfit || row.actual_profit || 0)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DashboardHome() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboardPeriod, setDashboardPeriod] = useState("month");
  const [warehouseId, setWarehouseId] = useState("");
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [trendItems, setTrendItems] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [productsSummary, setProductsSummary] = useState(null);
  const [postersSummary, setPostersSummary] = useState(null);
  const [broadcastsSummary, setBroadcastsSummary] = useState(null);

  const [usersSummary, setUsersSummary] = useState(null);
  const [employeesSummary, setEmployeesSummary] = useState(null);
  const [driversSummary, setDriversSummary] = useState(null);
  const [stockHealthSummary, setStockHealthSummary] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const analyticsRange = getDashboardAnalyticsRange(dashboardPeriod);

     const [
  reportSummaryRes,
  topProductsRes,
  ordersRes,
  usersSummaryRes,
  employeesSummaryRes,
  driversSummaryRes,
  stockHealthSummaryRes,
  productsSummaryRes,
  postersSummaryRes,
  broadcastsSummaryRes,
  activitiesRes,
] = await Promise.allSettled([
        ReportsService.summary({
          period: dashboardPeriod,
          warehouseId: warehouseId || undefined,
        }),

        orderAnalyticsService.getProducts({
          warehouseId,
          paymentStatus: "PAID",
          type: "top",
          limit: 5,
          ...analyticsRange,
        }),

        orderAnalyticsService.listOrders({
          warehouseId,
          paymentStatus: "PAID",
          page: 1,
          limit: 8,
          ...analyticsRange,
        }),

        warehouseId ? Promise.resolve(null) : dashboardSummaryService.getUsersSummary(),

        dashboardSummaryService.getEmployeesSummary(warehouseId ? { warehouseId } : {}),

        warehouseId ? Promise.resolve(null) : dashboardSummaryService.getDeliveryPartnersSummary(),

        dashboardSummaryService.getStockHealthSummary(warehouseId ? { warehouseId } : {}),

        dashboardSummaryService.getProductsSummary(),

        dashboardSummaryService.getPostersSummary(),

        dashboardSummaryService.getBroadcastsSummary(),

        NotificationsService.list(null, {
          limit: 8,
          offset: 0,
        }),
      ]);

      const reportSummaryData = getSettledValue(reportSummaryRes, {});
const reportData =
  reportSummaryData?.data?.data ||
  reportSummaryData?.data ||
  reportSummaryData ||
  {};

const reportKpis = reportData?.kpis || {};
const reportTrend = Array.isArray(reportData?.trend) ? reportData.trend : [];
      const topProductData = getSettledValue(topProductsRes, {});
      const orderData = getSettledValue(ordersRes, {});
      const usersSummaryData = warehouseId
        ? scopedUsersSummary(reportKpis)
        : getSettledValue(usersSummaryRes, null);
      const employeesSummaryData = getSettledValue(employeesSummaryRes, null);
      const driversSummaryData = warehouseId
        ? scopedDriversSummary(reportKpis)
        : getSettledValue(driversSummaryRes, null);
      const stockHealthSummaryData = getSettledValue(stockHealthSummaryRes, null);
      const productsSummaryData = getSettledValue(productsSummaryRes, null);
      const postersSummaryData = getSettledValue(postersSummaryRes, null);
      const broadcastsSummaryData = getSettledValue(broadcastsSummaryRes, null);
      const activityData = getSettledValue(activitiesRes, {});

      const orderList = pickArray(orderData);
      const activityList = pickArray(activityData);

      setSummary({
  totalRevenue: reportKpis.revenue,
  salesAmount: reportKpis.sales_amount,
  purchaseCost: reportKpis.purchase_cost,
  actualProfit: reportKpis.actual_profit,
  totalOrders: reportKpis.total_orders,
  deliveredOrders: reportKpis.delivered_orders,
  avgOrderValue: reportKpis.avg_order_value,
  ...reportKpis,
});

setTrendItems(reportTrend.slice(-7));
      setTopProducts(pickArray(topProductData).slice(0, 5));

      setOrders(orderList);
      setOrdersTotal(pickTotal(orderData, orderList));

      setUsersSummary(usersSummaryData);
      setEmployeesSummary(employeesSummaryData);
      setDriversSummary(driversSummaryData);
      setStockHealthSummary(stockHealthSummaryData);
      setProductsSummary(productsSummaryData);
      setPostersSummary(postersSummaryData);
      setBroadcastsSummary(broadcastsSummaryData);
      setActivities(activityList);
    } catch (err) {
      setError(err?.message || "Dashboard load failed.");
    } finally {
      setLoading(false);
    }
  }, [dashboardPeriod, warehouseId]);

  useEffect(() => {
    let mounted = true;
    settingsService.getWarehouseFilterOptions({ status: "active" })
      .then((res) => {
        if (!mounted) return;
        setWarehouseOptions(warehouseRowsOf(res).map((item) => ({
          id: item.id || item._id || item.warehouseId || item.warehouse_id,
          name: item.name || item.warehouseName || item.warehouse_name || item.code || item.id,
          code: item.code || "",
          city: item.city || "",
        })).filter((item) => item.id));
      })
      .catch((err) => console.warn("Warehouse filter options failed", err));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (warehouseId) return undefined;
    const intervalId = window.setInterval(async () => {
      try {
        const liveDrivers = await dashboardSummaryService.getDeliveryPartnersSummary();
        setDriversSummary(liveDrivers);
      } catch (err) {
        console.warn("Driver live summary refresh failed", err);
      }
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [warehouseId]);

  // Total collection = complete paid order amount / grand total.
  // This can include delivery charge, tax, wallet/coupon impact, etc.
  const totalRevenue = useMemo(() => {
    return toNumber(
      summary?.totalRevenue ??
      summary?.total_revenue ??
      summary?.totalCollection ??
      summary?.total_collection ??
      summary?.revenue ??
      summary?.grandTotal ??
      summary?.grand_total ??
      orders.reduce((sum, item) => sum + getOrderAmount(item), 0)
    );
  }, [summary, orders]);

  // Product sales = only sold product/item amount.
  // Actual profit must be calculated from this, not from grand total.
  const productSales = useMemo(() => {
    return toNumber(
      summary?.salesAmount ??
      summary?.sales_amount ??
      summary?.productSales ??
      summary?.product_sales ??
      summary?.itemSales ??
      summary?.item_sales ??
      summary?.itemsTotal ??
      summary?.items_total ??
      summary?.subtotal ??
      summary?.sub_total ??
      summary?.totalSales ??
      summary?.total_sales ??
      totalRevenue
    );
  }, [summary, totalRevenue]);

  const purchaseCost = useMemo(() => {
    return toNumber(
      summary?.purchaseCost ??
      summary?.purchase_cost ??
      summary?.totalPurchaseCost ??
      summary?.total_purchase_cost ??
      0
    );
  }, [summary]);

  const actualProfit = useMemo(() => {
    const hasDirectProfit =
      hasSummaryValue(summary, "actualProfit") ||
      hasSummaryValue(summary, "actual_profit") ||
      hasSummaryValue(summary, "grossProfit") ||
      hasSummaryValue(summary, "gross_profit");

    if (hasDirectProfit) {
      return toNumber(
        summary?.actualProfit ??
        summary?.actual_profit ??
        summary?.grossProfit ??
        summary?.gross_profit ??
        0
      );
    }

    return productSales - purchaseCost;
  }, [summary, productSales, purchaseCost]);

  const totalOrders = useMemo(() => {
    return toNumber(
      summary?.totalOrders ??
      summary?.total_orders ??
      summary?.orderCount ??
      summary?.order_count ??
      summary?.orders ??
      ordersTotal ??
      orders.length
    );
  }, [summary, ordersTotal, orders]);

  const avgOrderValue = useMemo(() => {
    const direct = toNumber(
      summary?.avgOrderValue ||
      summary?.avg_order_value ||
      summary?.averageOrderValue ||
      summary?.average_order_value
    );

    if (direct) return direct;
    if (!totalOrders) return 0;

    return Math.round(totalRevenue / totalOrders);
  }, [summary, totalRevenue, totalOrders]);

  const activeUsers = useMemo(() => {
    if (hasSummaryValue(summary, "active_users")) {
      return toNumber(summary?.active_users);
    }
    return toNumber(usersSummary?.activeUsers);
  }, [summary, usersSummary]);

  const activeDrivers = useMemo(() => {
    return toNumber(driversSummary?.activeDrivers);
  }, [driversSummary]);

  const inactiveDrivers = useMemo(() => {
    return toNumber(driversSummary?.inactiveDrivers);
  }, [driversSummary]);

  const activeEmployees = useMemo(() => {
    return toNumber(employeesSummary?.activeEmployees);
  }, [employeesSummary]);

  const pendingEmployees = useMemo(() => {
    return toNumber(employeesSummary?.pendingApprovals);
  }, [employeesSummary]);


  const totalUsersForDashboard = useMemo(() => {
    return toNumber(usersSummary?.totalUsers);
  }, [usersSummary]);

  const totalEmployeesForDashboard = useMemo(() => {
    return toNumber(employeesSummary?.totalEmployees);
  }, [employeesSummary]);

  const totalDriversForDashboard = useMemo(() => {
    return toNumber(driversSummary?.totalDrivers);
  }, [driversSummary]);

  const totalProductsForDashboard = useMemo(() => {
    return toNumber(productsSummary?.totalProducts);
  }, [productsSummary]);

  const activeProducts = useMemo(() => {
    return toNumber(productsSummary?.activeProducts);
  }, [productsSummary]);

  const activePosters = useMemo(() => {
    return toNumber(postersSummary?.activePosters);
  }, [postersSummary]);

  const activeBroadcasts = useMemo(() => {
    return toNumber(
      broadcastsSummary?.currentlyLiveBroadcasts || broadcastsSummary?.activeBroadcasts
    );
  }, [broadcastsSummary]);

  const lowStockCount = useMemo(() => {
    return toNumber(stockHealthSummary?.lowStock);
  }, [stockHealthSummary]);

  const outOfStockCount = useMemo(() => {
    return toNumber(stockHealthSummary?.outOfStock);
  }, [stockHealthSummary]);

  const expiringSoonCount = useMemo(() => {
    return toNumber(stockHealthSummary?.expiringSoon);
  }, [stockHealthSummary]);

  const orderRows = useMemo(() => {
    const pending = toNumber(
      summary?.pendingOrders ?? summary?.pending_orders ?? summary?.pending ?? 0
    );

    const accepted = toNumber(
      summary?.acceptedOrders ??
      summary?.accepted_orders ??
      summary?.processingOrders ??
      summary?.processing_orders ??
      summary?.activeOrders ??
      summary?.active_orders ??
      summary?.accepted ??
      0
    );

    const delivered = toNumber(
      summary?.deliveredOrders ??
      summary?.delivered_orders ??
      summary?.delivered ??
      0
    );

    const cancelled = toNumber(
      summary?.cancelledOrFailedOrders ??
      summary?.cancelled_or_failed_orders ??
      summary?.cancelledOrders ??
      summary?.cancelled_orders ??
      summary?.canceledOrders ??
      summary?.canceled_orders ??
      summary?.cancelled ??
      summary?.canceled ??
      0
    );

    const total = Math.max(
      toNumber(summary?.totalOrders ?? summary?.total_orders ?? totalOrders),
      pending + accepted + delivered + cancelled,
      1
    );

    return [
      { label: "Pending", value: pending },
      { label: "Accepted", value: accepted },
      { label: "Delivered", value: delivered },
      { label: "Cancelled / Failed", value: cancelled },
    ].map((item) => ({
      ...item,
      percent: `(${Math.round((item.value / total) * 100)}%)`,
    }));
  }, [summary, totalOrders]);

  const driverRows = useMemo(() => {
    const total = Math.max(totalDriversForDashboard, 1);
    const busy = toNumber(driversSummary?.busyDrivers);
    const offline = toNumber(driversSummary?.offlineDrivers);

    return [
      { label: "Active", value: activeDrivers },
      { label: "Busy", value: busy },
      { label: "Offline", value: offline },
      { label: "Inactive", value: inactiveDrivers },
    ].map((item) => ({
      ...item,
      percent: `(${Math.round((item.value / total) * 100)}%)`,
    }));
  }, [driversSummary, totalDriversForDashboard, activeDrivers, inactiveDrivers]);

  const dashboardPeriodLabel = dashboardPeriod === "month" ? "Current Month" : "All Time";
  const selectedWarehouse = warehouseOptions.find((item) => String(item.id) === String(warehouseId));
  const dashboardScopeLabel = warehouseId ? (selectedWarehouse?.name || "Selected warehouse") : "All warehouses";



  return (
    <div className="relative space-y-6 pb-10">
      <section className="dashboard-premium-hero relative isolate overflow-hidden rounded-[28px] border border-white/90 bg-[radial-gradient(circle_at_88%_8%,rgba(255,183,200,0.25),transparent_25%),radial-gradient(circle_at_9%_0%,rgba(208,213,255,0.18),transparent_28%),linear-gradient(120deg,#ffffff_0%,#fffafb_50%,#fff4f7_100%)] px-5 py-6 shadow-[0_22px_55px_rgba(34,45,82,0.09)] ring-1 ring-slate-200/70 sm:px-7 sm:py-7 lg:px-8 xl:min-h-[410px] xl:py-8">
        <div className="dashboard-hero-orb pointer-events-none absolute -right-12 -top-20 h-64 w-64 rounded-full bg-gradient-to-br from-orange-200/20 via-rose-200/30 to-violet-200/25 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 right-[20%] h-72 w-72 rounded-full bg-[#fbd7df]/20 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 hidden h-[145px] w-[190px] overflow-hidden xl:block">
          <img src={dashboardGroceryBag} alt="" className="absolute -left-[52px] top-0 w-[250px] max-w-none scale-x-[-1] drop-shadow-[0_14px_22px_rgba(84,48,46,0.10)]" />
        </div>
        <div aria-hidden="true" className="pointer-events-none absolute bottom-[129px] left-6 z-10 hidden xl:block">
          <p className="rotate-[-5deg] text-center text-[12px] font-semibold italic leading-[1.35] text-slate-400">Good Food<br />Brighter Business</p>
          <span className="mx-auto mt-2 block h-[2px] w-12 rotate-[-12deg] rounded-full bg-[#f46c82]" />
        </div>
        <div aria-hidden="true" className="dashboard-grocery-art pointer-events-none absolute bottom-0 right-3 z-10 hidden h-[300px] w-[320px] xl:block">
          <span className="absolute right-0 top-10 z-10 rotate-[-7deg] text-right text-[11px] font-semibold italic leading-[1.25] text-slate-400">Fresh Insights<br />Every Day</span>
          <img src={dashboardGroceryBag} alt="" className="absolute -bottom-7 right-3 w-[300px] max-w-none drop-shadow-[0_20px_28px_rgba(84,48,46,0.16)]" />
        </div>

        <div className="relative z-30 grid gap-7 xl:grid-cols-[minmax(320px,0.85fr)_minmax(610px,1.55fr)] xl:items-start">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="dashboard-hero-reveal inline-flex items-center gap-2 rounded-full border border-rose-200/80 bg-white/80 px-3.5 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-[#e05670] shadow-sm backdrop-blur">
                <BarChart3 size={13} /> Live business overview
              </div>
              <div className="dashboard-hero-reveal inline-flex items-center gap-2 rounded-full bg-emerald-50/90 px-3 py-1.5 text-[10px] font-extrabold text-emerald-600">
                <span className="relative flex h-2 w-2" aria-hidden="true"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-40 motion-reduce:animate-none" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
                Live
              </div>
            </div>
            <h1 className="dashboard-hero-reveal mt-4 text-[clamp(32px,3.2vw,48px)] font-black leading-[0.98] tracking-[-0.05em] text-[#101828]">
              <span className="block">Nefazo</span>
              <span className="dashboard-gradient-title mt-1 block">Dashboard</span>
            </h1>
            {loading ? (
              <p className="dashboard-hero-reveal mt-4 text-[12px] font-medium text-slate-400">Loading live admin dashboard...</p>
            ) : error ? (
              <p className="dashboard-hero-reveal mt-4 text-[12px] font-semibold text-red-500">{error}</p>
            ) : (
              <p className="dashboard-hero-reveal mt-4 text-[12px] font-semibold leading-relaxed text-slate-500 sm:text-[13px]">
                {dashboardScopeLabel} · {dashboardPeriodLabel} · Live operations and financial snapshot
              </p>
            )}
          </div>

          <div className="dashboard-hero-reveal grid items-start gap-3 sm:grid-cols-2 xl:mt-[34px] xl:grid-cols-[1fr_1fr_0.95fr]">
            <DashboardFilterSelect
              icon={Building2}
              label="Warehouse"
              value={warehouseId}
              onChange={setWarehouseId}
              ariaLabel="Warehouse filter"
              options={[
                { value: "", label: "All warehouses" },
                ...warehouseOptions.map((option) => ({
                  value: option.id,
                  label: `${option.name}${option.city ? ` · ${option.city}` : ""}`,
                })),
              ]}
            />
            <DashboardFilterSelect
              icon={CalendarClock}
              label="Time range"
              value={dashboardPeriod}
              onChange={setDashboardPeriod}
              ariaLabel="Dashboard period"
              options={DASHBOARD_PERIOD_OPTIONS}
            />
            <div className="sm:col-span-2 xl:col-span-1">
              <button type="button" onClick={loadDashboard} className="group/refresh relative inline-flex h-[62px] w-full items-center justify-center gap-2.5 overflow-hidden rounded-[15px] bg-[#d8687b] px-4 text-[12px] font-black text-white shadow-[0_10px_22px_rgba(17,26,53,0.20)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c95b69] hover:shadow-[0_14px_28px_rgba(17,26,53,0.26)] active:translate-y-0">
                <span aria-hidden="true" className="absolute inset-y-0 -left-1/2 w-1/3 skew-x-[-20deg] bg-white/10 transition-all duration-700 group-hover/refresh:left-[120%]" />
                <RefreshCcw size={15} className={loading ? "animate-spin" : "transition-transform duration-500 group-hover/refresh:rotate-180"} />
                <span className="relative">Refresh Data</span>
              </button>
              <p className="mt-1.5 text-center text-[9px] font-semibold text-slate-400">Last updated: Just now</p>
            </div>
          </div>
        </div>

        <div className="relative z-20 mt-6 grid gap-4 sm:grid-cols-2 xl:absolute xl:bottom-10 xl:left-[175px] xl:right-[300px] xl:mt-0 2xl:left-[210px] 2xl:right-[360px]">
          <button
            onClick={() => navigate("/admin/dashboard/employees")}
            type="button"
            className="group/action relative flex min-h-[92px] items-center gap-3.5 overflow-hidden rounded-[17px] border border-white bg-white/80 px-5 py-4 text-left shadow-[0_10px_30px_rgba(45,55,90,0.07)] ring-1 ring-[#f2e6e8] backdrop-blur-sm transition-all duration-300 before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-[#ff5470] hover:-translate-y-1 hover:shadow-[0_16px_34px_rgba(216,106,120,0.14)]"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-[15px] border border-[#ffd2d9] bg-[#fff0f3] text-[#f1526b] transition-transform duration-300 group-hover/action:scale-105"><UserCheck size={23} /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-black uppercase tracking-[0.08em] text-slate-400">Pending approvals</span>
              <span className="mt-1 block text-[15px] font-black text-slate-900">{pendingEmployees} employee request{pendingEmployees === 1 ? "" : "s"}</span>
              <span className="mt-1 block text-[11px] font-medium text-slate-400">Review and approve pending requests</span>
            </span>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#f7d9de] bg-[#fff3f5] text-slate-700 transition-all duration-300 group-hover/action:translate-x-1 group-hover/action:bg-[#ffe9ed] group-hover/action:text-[#d86a78]"><ArrowRight size={15} /></span>
          </button>
          <button
            onClick={() => navigate("/admin/dashboard/delivery-partners")}
            type="button"
            className="group/action relative flex min-h-[92px] items-center gap-3.5 overflow-hidden rounded-[17px] border border-white bg-white/80 px-5 py-4 text-left shadow-[0_10px_30px_rgba(45,55,90,0.07)] ring-1 ring-[#f2e6e8] backdrop-blur-sm transition-all duration-300 before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-amber-400 hover:-translate-y-1 hover:shadow-[0_16px_34px_rgba(245,158,11,0.13)]"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-[15px] border border-amber-200 bg-amber-50 text-amber-500 transition-transform duration-300 group-hover/action:scale-105"><Truck size={23} /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-black uppercase tracking-[0.08em] text-slate-400">Driver attention</span>
              <span className="mt-1 block text-[15px] font-black text-slate-900">{inactiveDrivers} inactive driver{inactiveDrivers === 1 ? "" : "s"}</span>
              <span className="mt-1 block text-[11px] font-medium text-slate-400">Drivers need your attention</span>
            </span>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-amber-100 bg-amber-50 text-slate-700 transition-all duration-300 group-hover/action:translate-x-1 group-hover/action:bg-amber-100 group-hover/action:text-amber-600"><ArrowRight size={15} /></span>
          </button>
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">Financial snapshot</p>
            <h2 className="mt-1 text-lg font-black text-slate-950">Business performance</h2>
          </div>
          <p className="hidden text-[11px] font-semibold text-slate-400 sm:block">{dashboardScopeLabel} · {dashboardPeriodLabel}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={IndianRupee} value={formatCurrency(totalRevenue)} label="Total collection" change={dashboardPeriodLabel} tone="pink" />
          <StatCard icon={BadgeIndianRupee} value={formatCurrency(productSales)} label="Product sales" change="Customer item sales" tone="green" />
          <StatCard icon={Package} value={formatCurrency(purchaseCost)} label="Purchase cost" change="Cost of sold inventory" tone="amber" />
          <StatCard icon={Tags} value={formatCurrency(actualProfit)} label="Actual profit" change="Collection minus tracked costs" tone="violet" />
        </div>
      </section>

      <section>
        <div className="mb-3">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">Live operations</p>
          <h2 className="mt-1 text-lg font-black text-slate-950">Operational pulse</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <StatCard icon={ShoppingCart} value={totalOrders} label="Total orders" change={dashboardPeriodLabel} tone="blue" compact />
          <StatCard icon={Users} value={activeUsers} label="Active users" change={`${totalUsersForDashboard} total users`} tone="green" compact />
          <StatCard icon={Boxes} value={activeProducts || totalProductsForDashboard} label="Products" change={warehouseId ? "Global catalog" : "Live catalog"} tone="violet" compact />
          <StatCard icon={UserCheck} value={activeEmployees || totalEmployeesForDashboard} label="Employees" change={`${pendingEmployees} pending approval`} tone="pink" compact />
          <StatCard icon={Truck} value={activeDrivers} label="Active drivers" change={`${inactiveDrivers} inactive`} tone="amber" compact />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <DonutChart
          title="Order status"
          centerValue={totalOrders}
          centerLabel="Total orders"
          rows={orderRows}
        />

        <DonutChart
          title="Driver status"
          centerValue={totalDriversForDashboard}
          centerLabel="Total drivers"
          rows={driverRows}
          variant="drivers"
        />

        <AdminQuickSummary
          productCount={activeProducts || totalProductsForDashboard}
          pendingEmployees={pendingEmployees}
          activePosters={activePosters}
          activeBroadcasts={activeBroadcasts}
          onProducts={() => navigate("/admin/dashboard/products")}
          onEmployees={() => navigate("/admin/dashboard/employees")}
          onPosters={() => navigate("/admin/dashboard/posters")}
          onBroadcasts={() => navigate("/admin/dashboard/broadcast-messages")}
        />
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <TrendRevenue items={trendItems} />
        <OrderTrend items={trendItems} />
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <StockHealth
          lowStockCount={lowStockCount}
          expiringSoonCount={expiringSoonCount}
          outOfStockCount={outOfStockCount}
          onLowStock={() => navigate("/admin/dashboard/stock")}
          onExpiringSoon={() => navigate("/admin/dashboard/stock")}
          onOutOfStock={() => navigate("/admin/dashboard/stock")}
        />
        <div className="xl:col-span-2">
          <RecentOrdersTable
            items={orders}
            onViewAll={() => navigate("/admin/dashboard/reports")}
          />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
        <TopProductsTable
          items={topProducts}
          onViewAll={() => navigate("/admin/dashboard/products")}
        />

        <div className="space-y-4">
          <RecentActivities
            items={activities}
            onViewAll={() => navigate("/admin/dashboard/notifications")}
          />

          <div className="rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-slate-200/70">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">Shortcuts</p>
                <h3 className="mt-1 text-[16px] font-black text-slate-950">Admin actions</h3>
              </div>
              <ArrowRight size={16} className="text-[#d86a78]" />
            </div>

            <div className="mt-4 grid gap-2">
              {[
                ["Add new product", "/admin/dashboard/products/new"],
                ["Add employee", "/admin/dashboard/employees/new"],
                ["Create broadcast", "/admin/dashboard/broadcast-messages"],
              ].map(([label, to]) => (
                <button
                  key={to}
                  type="button"
                  onClick={() => navigate(to)}
                  className="group flex items-center justify-between rounded-[14px] border border-slate-200/80 bg-gradient-to-r from-slate-50/80 to-white px-4 py-3 text-left text-[12px] font-bold text-slate-700 shadow-[0_5px_14px_rgba(15,23,42,0.035)] transition-all hover:-translate-y-0.5 hover:border-rose-200 hover:bg-rose-50/40 hover:shadow-[0_8px_18px_rgba(15,23,42,0.06)]"
                >
                  {label}
                  <ArrowRight size={14} className="text-[#d86a78]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
